import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Mentor } from '../../../mocks/mentors';
import { api } from '@/lib/api';
import { adaptLecturer } from '@/lib/adapters';
import Avatar from '@/components/base/Avatar';

interface Props {
  currentMentor: Mentor;
}

const RelatedMentors = ({ currentMentor }: Props) => {
  const navigate = useNavigate();
  const [allMentors, setAllMentors] = useState<Mentor[]>([]);

  useEffect(() => {
    void api.searchLecturers({ pageCurrent: 1, pageSize: 20 }).then((page) => {
      setAllMentors(page.list.map(adaptLecturer));
    });
  }, []);

  const related = useMemo(() => {
    const sameCategory = allMentors.filter((mentor) => mentor.id !== currentMentor.id && mentor.category === currentMentor.category);
    const rest = allMentors.filter((mentor) => mentor.id !== currentMentor.id && mentor.category !== currentMentor.category);
    return [...sameCategory, ...rest].slice(0, 3);
  }, [allMentors, currentMentor]);

  if (!related.length) return null;

  return (
    <section className="mt-8">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-1 h-6 bg-violet-600 rounded-full" />
          <div>
            <h2 className="text-base font-bold text-[#0A1628]">你可能也感興趣</h2>
            <p className="text-xs text-slate-400 mt-0.5">根據真實講師資料推薦</p>
          </div>
        </div>
        <button onClick={() => navigate('/mentors')} className="text-xs text-violet-600 font-semibold hover:text-violet-700">
          瀏覽全部導師
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {related.map((mentor) => (
          <button
            key={mentor.id}
            onClick={() => navigate(`/mentor/${mentor.id}`)}
            className="bg-white rounded-2xl border border-slate-100 hover:border-violet-200 transition-all overflow-hidden text-left"
          >
            <div className="relative h-40 overflow-hidden">
              <Avatar src={mentor.avatar} alt={mentor.name} className="w-full h-full" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-3 left-3 right-3">
                <p className="text-white font-bold text-sm truncate">{mentor.name}</p>
                <p className="text-white/70 text-xs truncate">{mentor.title}</p>
              </div>
            </div>
            <div className="p-4">
              <div className="flex flex-wrap gap-1.5 mb-3">
                {mentor.skills.slice(0, 3).map((skill) => (
                  <span key={skill} className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-slate-100 text-slate-500">
                    {skill}
                  </span>
                ))}
              </div>
              <div className="flex items-center justify-between text-xs text-slate-400">
                <span>${mentor.price}/次</span>
                <span>{mentor.rating} 分</span>
              </div>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
};

export default RelatedMentors;
